import React, { useContext, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import CoreListNavBadge from "../../components/CoreListNavBadge";
import NotificationsNavBadge from "../../notifications/NotificationsNavBadge";
import { UserContext, useEntitlements } from "../../contexts";
import { isRcOrDaoStaff } from "../../core/config/storeId";
import { useOnMasterStore } from "../../helpers/storeScope";
import { useSidebar } from "./SidebarContext";
import StoreSwitcher from "../../components/StoreSwitcher";

import "./rc-admin.scss";

const IClose = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M4 4l8 8M12 4L4 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

// ── Icons ──────────────────────────────────────────────────────────────────────

const IOverview = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7" rx="1"/>
    <rect x="14" y="3" width="7" height="7" rx="1"/>
    <rect x="3" y="14" width="7" height="7" rx="1"/>
    <rect x="14" y="14" width="7" height="7" rx="1"/>
  </svg>
);

const IListings = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2L2 7l10 5 10-5-10-5z"/>
    <path d="M2 17l10 5 10-5"/>
    <path d="M2 12l10 5 10-5"/>
  </svg>
);

// Funnel — a pipeline, not a person. The previous card-with-a-head glyph read
// as a second "Users" entry next to the real one.
const ILeads = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 4h18l-7 8.5V19l-4 2v-8.5L3 4z"/>
  </svg>
);

const IOrders = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
    <path d="M14 2v6h6"/>
    <line x1="16" y1="13" x2="8" y2="13"/>
    <line x1="16" y1="17" x2="8" y2="17"/>
    <line x1="10" y1="9" x2="8" y2="9"/>
  </svg>
);

const IUsers = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 00-3-3.87"/>
    <path d="M16 3.13a4 4 0 010 7.75"/>
  </svg>
);

const IAnalytics = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="20" x2="18" y2="10"/>
    <line x1="12" y1="20" x2="12" y2="4"/>
    <line x1="6" y1="20" x2="6" y2="14"/>
  </svg>
);

const IMyStore = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
    <polyline points="9 22 9 12 15 12 15 22"/>
  </svg>
);

const ISettings = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3"/>
    <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/>
  </svg>
);

const IUptime = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 12h4l2-6 4 12 2-6h6"/>
  </svg>
);

const IDashboard = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9.75L12 3l9 6.75V21a1 1 0 01-1 1H4a1 1 0 01-1-1V9.75z"/>
    <path d="M9 22V12h6v10"/>
  </svg>
);

const IShop = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 7h16l-1.5 9.5a2 2 0 01-2 1.5H7.5a2 2 0 01-2-1.5L4 7z"/>
    <path d="M4 7l1-4h14l1 4"/>
    <circle cx="9" cy="20" r="1"/>
    <circle cx="15" cy="20" r="1"/>
  </svg>
);

const IWishlist = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/>
  </svg>
);

const IStock = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 7H4a2 2 0 00-2 2v10a2 2 0 002 2h16a2 2 0 002-2V9a2 2 0 00-2-2z"/>
    <path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/>
  </svg>
);

const IKanban = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="5" height="18" rx="1"/>
    <rect x="10" y="3" width="5" height="12" rx="1"/>
    <rect x="17" y="3" width="4" height="15" rx="1"/>
  </svg>
);

const IAlerts = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8a6 6 0 00-12 0c0 7-3 9-3 9h18s-3-2-3-9"/>
    <path d="M13.73 21a2 2 0 01-3.46 0"/>
  </svg>
);

const IBook = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 19.5A2.5 2.5 0 016.5 17H20"/>
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/>
  </svg>
);

const IConsignment = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="7" width="20" height="14" rx="2"/>
    <path d="M16 7V5a2 2 0 00-4 0v2"/>
    <path d="M8 7V5a2 2 0 00-4 0"/>
    <line x1="12" y1="12" x2="12" y2="16"/>
    <line x1="10" y1="14" x2="14" y2="14"/>
  </svg>
);


const ILogout = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/>
    <polyline points="16 17 21 12 16 7"/>
    <line x1="21" y1="12" x2="9" y2="12"/>
  </svg>
);

// Chevron for expand/collapse
const IChevron = ({ open }) => (
  <svg
    width="12" height="12" viewBox="0 0 12 12" fill="none"
    style={{ transition: "transform 200ms ease", transform: open ? "rotate(180deg)" : "rotate(0deg)", flexShrink: 0 }}
  >
    <path d="M2 4.5l4 4 4-4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// ── Nav config ─────────────────────────────────────────────────────────────────

const TOP_ITEMS = [
  { to: "/rc/listings",     icon: <IListings />,  label: "Listings"  },
  { to: "/requests/orders",    icon: <IOrders />,    label: "Orders"    },
  { to: "/kanban",          icon: <IKanban />,    label: "Kanban"    },
  { to: "/users/client",    icon: <IUsers />,     label: "Users"     },
  { to: "/leads/leads",  icon: <ILeads />,     label: "Leads"     },
  { to: "/analytics",       icon: <IAnalytics />, label: "Analytics" },
  // "Stock Alert", not "Stock": the page is now where every buy watch lives —
  // trade groups and marketplaces — so the rail entry names the alerting, not
  // the inventory. Buy Alerts is gone from this rail because Core List covers
  // it; /alerts still resolves for anyone holding a link (Alex, 2026-10-08).
  { to: "/stock/core-list", icon: <IStock />,     label: "Stock Alert", badge: <CoreListNavBadge /> },
];

const INotifications = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 12h-6l-2 3h-4l-2-3H2"/>
    <path d="M5.45 5.11L2 12v6a2 2 0 002 2h16a2 2 0 002-2v-6l-3.45-6.89A2 2 0 0016.76 4H7.24a2 2 0 00-1.79 1.11z"/>
  </svg>
);

const SECONDARY_ITEMS = [
  { to: "/rc/overview",    icon: <IOverview />,      label: "Overview"     },
  { to: "/dashboard",      icon: <IDashboard />,     label: "Dashboard"    },
  // The mobile drawer IS this sidebar — this entry is how the notification
  // center is reachable from the menu on a phone (the topbar bell covers
  // desktop). Inbox-tray icon on purpose: the bell shape already means
  // "Buy Alerts" in this rail.
  { to: "/notifications",  icon: <INotifications />, label: "Notifications", badge: <NotificationsNavBadge /> },
];

const DAO_WATCHES_ITEMS = [
  { to: "/products/watches",  icon: <IShop />,        label: "Shop"        },
  { to: "/products/wishlist", icon: <IWishlist />,    label: "Wishlist"    },
  { to: "/consignment",       icon: <IConsignment />, label: "Consignment" },
  { to: "/member-guidebook",  icon: <IBook />,        label: "Guidebook"   },
];

const BOTTOM_ITEMS = [
  { to: "/uptime",                      icon: <IUptime />,   label: "Uptime"   },
  { to: "/store-settings",              icon: <ISettings />, label: "Settings" },
  { to: "/logout",                      icon: <ILogout />,   label: "Log out"  },
];

// ── Sidebar ────────────────────────────────────────────────────────────────────

const RCSidebar = () => {
  const location = useLocation();
  const { user, activeStoreId } = useContext(UserContext);
  const [daoOpen, setDaoOpen] = useState(false);
  const { mobileNavOpen, closeMobileNav } = useSidebar();

  // The "Dao Watches" group is DAO-specific branding — only show it to the DAO
  // store itself or to RC master admins. Other tenants must never see it.
  // Follows an active-store switch (Phase 5 of the multi-store access
  // rework) — see isRcOrDaoStaff's own docblock.
  const showDaoGroup = !!user && isRcOrDaoStaff(user, activeStoreId);

  // "Master Store" (All Stores) is the unscoped master-admin view: no active
  // store. Analytics is a single-store report, so it has nothing coherent to
  // show across every store at once — hide the entry until a store is picked.
  // Predicate shared with the write guard and the Master Store banner.
  const onMasterStore = useOnMasterStore();
  // Plan gating: nav entries for features the store's plan doesn't include
  // disappear. The pages themselves are locked by Route's requireFeature, so
  // this is presentation only — a direct URL is still blocked.
  const { hasFeature } = useEntitlements();
  const topItems = (onMasterStore
    ? TOP_ITEMS.filter((item) => item.to !== "/analytics")
    : TOP_ITEMS
  )
    .filter((item) => item.to !== "/stock/core-list" || hasFeature("core_inventory_management|geo_arbitrage"))
    .filter((item) => item.to !== "/kanban" || hasFeature("kanban_list_view"))
    .filter((item) => item.to !== "/analytics" || hasFeature("analytics_core"));

  const isActive = (to) => location.pathname.startsWith(to);

  const daoIsActive = DAO_WATCHES_ITEMS.some((item) =>
    location.pathname.startsWith(item.to)
  );

  const renderLink = ({ to, href, icon, label, badge }) => {
    if (href) {
      return (
        <a
          key={href}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="rc-sidebar__link"
          title={label}
          onClick={closeMobileNav}
        >
          <span className="rc-sidebar__icon">{icon}</span>
          <span className="rc-sidebar__label">{label}</span>
          {badge}
        </a>
      );
    }
    return (
      <NavLink
        key={to}
        to={to}
        className={`rc-sidebar__link ${isActive(to) ? "rc-sidebar__link--active" : ""}`}
        title={label}
        onClick={closeMobileNav}
      >
        <span className="rc-sidebar__icon">{icon}</span>
        <span className="rc-sidebar__label">{label}</span>
        {badge}
      </NavLink>
    );
  };

  const renderDisabledLink = (item) =>
    import.meta.env.DEV ? (
      <NavLink
        key={item.to}
        to={item.to}
        className={`rc-sidebar__link rc-sidebar__link--disabled ${isActive(item.to) ? "rc-sidebar__link--active" : ""}`}
        title={item.label}
        onClick={closeMobileNav}
      >
        <span className="rc-sidebar__icon">{item.icon}</span>
        <span className="rc-sidebar__label">{item.label}</span>
      </NavLink>
    ) : (
      <span
        key={item.to}
        className="rc-sidebar__link rc-sidebar__link--disabled"
        title={item.label}
      >
        <span className="rc-sidebar__icon">{item.icon}</span>
        <span className="rc-sidebar__label">{item.label}</span>
      </span>
    );

  return (
    <aside
      className={`rc-sidebar${mobileNavOpen ? " rc-sidebar--open" : ""}`}
      aria-modal={mobileNavOpen || undefined}
      role={mobileNavOpen ? "dialog" : undefined}
      aria-label="Navigation"
    >
      {/* Logo + mobile close */}
      <div className="rc-sidebar__header">
        <div className="rc-sidebar__logo">
          <img src="/assets/graphics/Frame 14.png" alt="RC" className="rc-sidebar__logo-img" />
        </div>
        <button
          type="button"
          className="rc-sidebar__close"
          onClick={closeMobileNav}
          aria-label="Close menu"
        >
          <IClose />
        </button>
      </div>

      {/* Primary: Orders, Listings, Users, Leads */}
      <nav className="rc-sidebar__nav">
        {topItems.map(renderLink)}
      </nav>

      {/* Separator */}
      <div className="rc-sidebar__divider" />

      {/* Secondary pages — coming soon */}
      <nav className="rc-sidebar__nav rc-sidebar__nav--secondary">
        <div className="rc-sidebar__coming-soon">
          <span className="rc-sidebar__coming-soon-label">Coming soon</span>
        </div>

        {SECONDARY_ITEMS.map(renderDisabledLink)}

        {/* ── Dao Watches group (DAO / master only) ── */}
        {showDaoGroup && (
        <div className={`rc-sidebar__group${daoIsActive ? " rc-sidebar__group--active" : ""}`}>
          <button
            type="button"
            className="rc-sidebar__group-trigger rc-sidebar__link rc-sidebar__link--disabled"
            onClick={() => setDaoOpen((o) => !o)}
            title="Dao Watches"
          >
            <span className="rc-sidebar__icon" />
            <span className="rc-sidebar__label rc-sidebar__group-label">
              Dao Watches
            </span>
            <span className="rc-sidebar__group-chevron">
              <IChevron open={daoOpen} />
            </span>
          </button>

          {/* Sub-items */}
          <div className={`rc-sidebar__group-items${daoOpen ? " rc-sidebar__group-items--open" : ""}`}>
            {DAO_WATCHES_ITEMS.map(renderDisabledLink)}
          </div>
        </div>
        )}
      </nav>

      {/* Settings + Logout at bottom */}
      <nav className="rc-sidebar__nav rc-sidebar__nav--bottom">
        <StoreSwitcher />
        {BOTTOM_ITEMS
          // Uptime monitor (Feature 52) is Future Release and the page shows
          // every tenant's errors, so it stays an RC-staff tool.
          .filter((item) => item.to !== "/uptime" || hasFeature("uptime_monitor"))
          .map(renderLink)}
      </nav>
    </aside>
  );
};

export default RCSidebar;
